import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';

/**
 * Coleções do Firestore
 */
const EVALUATION_CONFIG_COLLECTION = 'avaliacoes_config';

/**
 * Obtém a configuração de opções para um projeto específico
 * @param {string} configId - ID da configuração (pode ser o nome do curso ou um ID único)
 * @returns {Promise<Object>} Configuração do projeto
 */
export const getEvaluationConfig = async (configId) => {
  try {
    const configRef = doc(db, EVALUATION_CONFIG_COLLECTION, configId);
    const configDoc = await getDoc(configRef);

    if (configDoc.exists()) {
      return {
        exists: true,
        ...configDoc.data(),
        id: configDoc.id
      };
    } else {
      return {
        exists: false,
        configId,
        positiveOptions: [],
        improvementOptions: [],
        active: true,
        createdAt: serverTimestamp()
      };
    }
  } catch (error) {
    console.error('Erro ao buscar configuração de avaliação:', error);
    throw error;
  }
};

/**
 * Salva a configuração de opções para um projeto específico
 * @param {string} configId - ID único da configuração
 * @param {Object} config - Configuração do projeto
 * @param {string} config.courseName - Nome do projeto
 * @param {string} config.userId - ID do usuário/participante
 * @param {Array} config.positiveOptions - Opções positivas selecionadas
 * @param {Array} config.improvementOptions - Opções de melhoria selecionadas
 * @param {boolean} config.active - Se a configuração está ativa
 * @returns {Promise<Object>} Resultado da operação
 */
export const saveEvaluationConfig = async (configId, config) => {
  try {
    const configRef = doc(db, EVALUATION_CONFIG_COLLECTION, configId);

    const dataToSave = {
      ...config,
      updatedAt: serverTimestamp()
    };

    await setDoc(configRef, dataToSave);

    return {
      success: true,
      message: 'Configuração salva com sucesso!',
      configId
    };
  } catch (error) {
    console.error('Erro ao salvar configuração de avaliação:', error);
    return {
      success: false,
      message: 'Erro ao salvar configuração: ' + error.message
    };
  }
};

/**
 * Atualiza parcialmente a configuração de um projeto
 * @param {string} courseName - Nome do projeto
 * @param {Object} updates - Atualizações a serem aplicadas
 * @returns {Promise<Object>} Resultado da operação
 */
export const updateEvaluationConfig = async (courseName, updates) => {
  try {
    const configRef = doc(db, EVALUATION_CONFIG_COLLECTION, courseName);

    await updateDoc(configRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return {
      success: true,
      message: 'Configuração atualizada com sucesso!',
      courseName
    };
  } catch (error) {
    console.error('Erro ao atualizar configuração de avaliação:', error);
    return {
      success: false,
      message: 'Erro ao atualizar configuração: ' + error.message
    };
  }
};

/**
 * Obtém todas as configurações de avaliação
 * @returns {Promise<Array>} Lista de configurações
 */
export const getAllEvaluationConfigs = async () => {
  try {
    // Esta função não pode ser implementada com a estrutura atual do Firestore
    // pois estamos usando documentos individuais com IDs específicos
    // Em vez disso, teríamos que implementar uma abordagem diferente
    // Por enquanto, retornamos uma lista vazia
    return [];
  } catch (error) {
    console.error('Erro ao buscar todas as configurações:', error);
    return [];
  }
};

export default {
  getEvaluationConfig,
  saveEvaluationConfig,
  updateEvaluationConfig,
  getAllEvaluationConfigs
};